import { matchPath } from 'react-router';
import queryString from 'querystring';

import matchClassicRoute from './match-classic-route';
import { getProductPageTabFromHash } from './shared';

const isConflictingRoute = pathname => {
  switch (pathname.split('/')[1]) {
    case 'news':
    case 'nyheter': {
      return true;
    }
    default:
      return false;
  }
};

export const generateKey = (name, variables) => {
  const sortedVariables = Object.keys(variables)
    .sort((a, b) => a.localeCompare(b))
    .reduce((all, key) => all + key + JSON.stringify(variables[key]), '');

  return name + JSON.stringify(sortedVariables);
};

export default ({
  useClassicUrls,
  useHashForProductTabs,
  routeConfig,
  localizedRoutes,
  location,
  marketCode,
  experiments,
}) => {
  const query = queryString.decode(location.search.substr(1));

  if (useClassicUrls) {
    const classicMatch = matchClassicRoute(
      location.pathname,
      query,
      location.hash,
      useHashForProductTabs
    );

    if (classicMatch) {
      const match = matchPath(location.pathname, {
        path: '*',
        exact: false,
      });

      match.params = classicMatch.params;
      const {
        component,
        metaTags,
        cacheControl,
        showError = true,
        isReactRouterRoute,
      } = routeConfig[classicMatch.route];

      const variables = component.variables
        ? component.variables({
            ...query,
            ...classicMatch.params,
            marketCode,
            experiments,
            /* The app feature level from .env config (APP_FEATURE_LEVEL). featureToggle is set
             * from "server/init.js". window.FEATURE_TOGGLE is set from "server/react/middelware/out.js".
             */
            featureToggle: FEATURE_TOGGLE,
          })
        : {};

      return {
        name: classicMatch.route,
        component,
        variables,
        metaTags,
        cacheControl,
        key: generateKey(classicMatch.route, variables),
        match,
        showError,
        isReactRouterRoute,
        ssr: component.ssr,
      };
    }
  }

  /**
   * Some classic routes are in conflict with our new routes.
   * Ex. "/news/whatever--something" will match the store route (--s:id).
   * Handle this special case here for now by simply bypassing the matchPath loop.
   */
  if (isConflictingRoute(location.pathname)) {
    return undefined;
  }

  /**
      This is very similar to what react-router switch does,
      simply run matchPath (which is used internally in react router)
      and return first match. Runs as a for loop to avoid unnessary iterations
    * */
  const routeKeys = Object.keys(routeConfig);

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < routeKeys.length; i++) {
    const name = routeKeys[i];

    const { path, exact = true } = routeConfig[name];

    const localizedPath = localizedRoutes[name]
      ? localizedRoutes[name].path
      : path;

    const match = matchPath(location.pathname, {
      path: localizedPath,
      exact,
    });

    if (match) {
      const {
        component,
        metaTags,
        cacheControl,
        showError = true,
        isReactRouterRoute,
      } = routeConfig[name];
      if (name === 'product' && useHashForProductTabs) {
        const section = getProductPageTabFromHash(location.hash);
        if (section !== 'main') {
          match.params.section = section;
        }
      }

      const variables = component.variables
        ? component.variables({
            ...query,
            ...match.params,
            marketCode,
            experiments,
            /* The app feature level from .env config (APP_FEATURE_LEVEL). featureToggle is set
             * from "server/init.js". window.FEATURE_TOGGLE is set from "server/react/middelware/out.js".
             */
            featureToggle: FEATURE_TOGGLE,
          })
        : {};

      return {
        name,
        component,
        variables,
        key: generateKey(name, variables),
        metaTags,
        cacheControl,
        match,
        showError,
        isReactRouterRoute,
        ssr: component.ssr,
      };
    }
  }

  return undefined;
};
